import { flatten, get } from "lodash";

import {
  aggregateBudgetMixSpendByPrimaryBreakdown,
  reverseMap,
} from "@features/mmm/utils/aggregate";
import {
  bucketSortByPredicate,
  subsortBucketedList,
} from "@features/mmm/utils/sort";
import {
  type BudgetMixBreakdownSpendDatum,
  type FullBudgetMixResponse,
} from "@north-beam/nb-common";

import { type HistoricalBudgetMixResponse } from "./card-container";

type OrderedFinalBreakdownMap = Map<string, BudgetMixBreakdownSpendDatum[]>;
type BreakdownDatumTrackingSet = Set<string>;

export function historicalAndRecommendedDataAligner(
  historicalData: HistoricalBudgetMixResponse,
  recommendedData: FullBudgetMixResponse[],
) {
  /* Purpose of this function:
     (1) Order recommended data in the same way the "historical" data is ordered
     (2) Ensure that the returned historical data and the recommended data have ALL of the same
         breakdown combinations, 0-filled when appropriate. This is specifically important
         for the table, so that the rows line up when expanding to see secondary breakdowns.
         This will also support the chart maintaining order when appropriate.
     (3) Return aggregated [to be shared] data from all charts to support UI dependent on states
         of adjacent charts, specifically 2nd-level-breakdown offsets */

  /* Steps:
     (1) Order the historical data as desired
     (2) Process the historical data in a ordered lookup table (to aid in adding datums later on)
     (3) Make a lookup table for recommended datasets
     (4) Create an array of sets of recommended datasets
     (5) Create an empty Map for each recommended dataset which will have the
         the structure of key: primaryBreakdown value; value: array of ordered BreakdownDatums.
         Use a Map so that order will be maintained and this can be easily flattened to a single
         array of correclty ordered BreakdownDatums
     (6) Iterate through the ordered historical dataset and use the lookup table for each
         recommended dataset to:
         (a) if the same primary/secondary breakdown combo exists in the recommended data, add the
             datum
           (i) When a datum is entered, remove it from the appropriate Set
         (b) if the primary/secondary breakdown combo does not exist for a recommended dataset, add
             it with a spend value of 0
     (7) Use the array of Sets to add the remaining datums in each recommended set to:
         (a) Their respective datasets at the end of the array of datums under the appropriate
             primaryBreakdown value
         (b) All other "recommended" datasets at the end of the appropriate primaryBreakdown keys:
            (i) If they exist, removing the datum from the appropriate set
            (ii) If not, with a spend value of 0
         (c) The "historical" dataset with the spend value of 0
     (8) Flatten and return the historical and recommended datasets. Their order should be
         consistent with each other.
     (9) Also return total spend per platform for each card (to be put into jotai shared state)
         [Note: This aggregation for current is a byproduct of step (1), add it to step (3) for
         recommended datasets] */

  /* Order
     TODO: The total spend aggregation step is done a good amount, try to consolidate it
     somewhere */
  const historicalPrimaryBreakdownSpendAggregateIndexedByPrimaryBreakdown =
    aggregateBudgetMixSpendByPrimaryBreakdown(historicalData.data.spend_data);
  const historicalPrimaryBreakdownsIndexedByAggregateSpend = reverseMap(
    historicalPrimaryBreakdownSpendAggregateIndexedByPrimaryBreakdown,
  );
  const historicalAggregateSortedSpend = Array.from(
    historicalPrimaryBreakdownsIndexedByAggregateSpend.keys(),
  ).sort((a, b) => b - a);

  /* NOTE: "unknown" should never happen, but will be handled by "generic" platform icon, force
     typing back to DesignElementKey */
  const historicalSortedPlatformsByAggregateSpend = flatten(
    historicalAggregateSortedSpend.map(
      (spendValue) =>
        historicalPrimaryBreakdownsIndexedByAggregateSpend.get(spendValue) || [
          "unknown",
        ],
    ),
  );

  const orderedHistoricalData = subsortBucketedList(
    bucketSortByPredicate(
      historicalData.data.spend_data,
      (datum) => datum.breakdowns[0].value,
      historicalSortedPlatformsByAggregateSpend,
    ),
    (a, b) => b.spend - a.spend,
  );

  // Preprocess
  const recommendedPrimaryBreakdownToSpendMap = recommendedData.map((data) =>
    aggregateBudgetMixSpendByPrimaryBreakdown(
      data.data.aggregated_data.spend_data,
    ),
  );
  const processedRecommendedData = recommendedData.map((data) =>
    preprocessData(data.data.aggregated_data.spend_data),
  );
  const recommendedDataSets: BreakdownDatumTrackingSet[] = recommendedData.map(
    (data) =>
      new Set(
        data.data.aggregated_data.spend_data.map((spendDatum) =>
          JSON.stringify(spendDatum),
        ),
      ),
  );

  // Set up empty datasets
  const orderedHistoricalDataset: OrderedFinalBreakdownMap = new Map();
  const orderedRecommendedDatasets: OrderedFinalBreakdownMap[] =
    recommendedData.map(() => new Map());

  // Fill out recommended data in correct order
  orderedHistoricalData.forEach((datum) => {
    const primaryBreakdownValue = datum.breakdowns[0].value;
    addDatumToPrimaryBreakdownMap(
      orderedHistoricalDataset,
      datum,
      primaryBreakdownValue,
    );
    for (
      let recommendedDataIndex = 0;
      recommendedDataIndex < recommendedData.length;
      recommendedDataIndex++
    ) {
      const processedRecommendedDataset =
        processedRecommendedData[recommendedDataIndex];
      const orderedRecommendedDataset =
        orderedRecommendedDatasets[recommendedDataIndex];
      const recommendedDataSet = recommendedDataSets[recommendedDataIndex];
      addDatumFromLookupMapOrZeroSpendDatumToFinalMap(
        processedRecommendedDataset,
        orderedRecommendedDataset,
        recommendedDataSet,
        datum.breakdowns,
        datum.data_column_name,
      );
    }
  });

  // Populate all datasets with leftover recommended datums
  for (
    let recommendedDataSetIndex = 0;
    recommendedDataSetIndex < recommendedDataSets.length;
    recommendedDataSetIndex++
  ) {
    const recommendedDataSet = recommendedDataSets[recommendedDataSetIndex];
    const remainingRecommendedDataSetIndices = [
      ...Array(recommendedDataSets.length).keys(),
    ].filter((i) => i > recommendedDataSetIndex);
    const recommendedDataFinalMap =
      orderedRecommendedDatasets[recommendedDataSetIndex];
    recommendedDataSet.forEach((datumString) => {
      const datum: BudgetMixBreakdownSpendDatum = JSON.parse(datumString);
      const primaryBreakdownValue = datum.breakdowns[0].value;
      addDatumToPrimaryBreakdownMap(
        recommendedDataFinalMap,
        datum,
        primaryBreakdownValue,
      );
      remainingRecommendedDataSetIndices.forEach(
        (remainingRecommendedDataSetIndex) => {
          const remainingRecommendedDataFinalMap =
            orderedRecommendedDatasets[remainingRecommendedDataSetIndex];
          const remainingRecommendedDataLookupMap =
            processedRecommendedData[remainingRecommendedDataSetIndex];
          const remainingRecommendedDataTrackingSet =
            recommendedDataSets[remainingRecommendedDataSetIndex];
          addDatumFromLookupMapOrZeroSpendDatumToFinalMap(
            remainingRecommendedDataLookupMap,
            remainingRecommendedDataFinalMap,
            remainingRecommendedDataTrackingSet,
            datum.breakdowns,
            datum.data_column_name,
          );
        },
      );
      const zeroSpendDatum = createDatumWithZeroSpend(
        datum.breakdowns,
        datum.data_column_name,
      );
      addDatumToPrimaryBreakdownMap(
        orderedHistoricalDataset,
        zeroSpendDatum,
        primaryBreakdownValue,
      );
    });
  }

  const aggregatedSpendsByPrimaryBreakdown = {
    historical:
      historicalPrimaryBreakdownSpendAggregateIndexedByPrimaryBreakdown,
    recommended: recommendedPrimaryBreakdownToSpendMap,
  };

  return {
    historicalData: {
      ...historicalData,
      data: {
        ...historicalData.data,
        spend_data: flattenFinalOrderedMap(orderedHistoricalDataset),
      },
    },
    recommendedData: orderedRecommendedDatasets.map((dataset, i) => {
      const recommendedBudgetMix = recommendedData[i];
      return {
        ...recommendedBudgetMix,
        data: {
          ...recommendedBudgetMix.data,
          aggregated_data: {
            ...recommendedBudgetMix.data.aggregated_data,
            spend_data: flattenFinalOrderedMap(dataset),
          },
        },
      };
    }),
    aggregatedSpendsByPrimaryBreakdown,
  };
}

type ProcessedRecommendedData =
  | Record<string, Record<string, BudgetMixBreakdownSpendDatum>>
  | Record<string, BudgetMixBreakdownSpendDatum>;

function preprocessData(data: BudgetMixBreakdownSpendDatum[]) {
  /* Shape:
      {
        primaryBreakdownValue1: {
          secondaryBreakdownValue1: BreakdownDatum,
          secondaryBreakdownValue2: BreakdownDatum,
          ...
       },
       primaryBreakdownValue2: {
         secondaryBreakdownValue3: BreakdownDatum,
         secondaryBreakdownValue4: BreakdownDatum,
         ...
       },
       ...
     }
     The purpose of this is to allow quick lookup times of recommended data when accomplishing the
     steps of the above function, see comments. This prevents O(n^2) ordering of recommended
     data. */

  return data.reduce(
    (
      processedData: ProcessedRecommendedData,
      datum: BudgetMixBreakdownSpendDatum,
    ) => {
      const [breakdown1, breakdown2] = datum.breakdowns;
      const primaryBreakdownValue = breakdown1.value;
      const secondaryBreakdownValue = breakdown2?.value;

      if (primaryBreakdownValue && secondaryBreakdownValue) {
        if (typeof processedData[primaryBreakdownValue] === "object") {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          processedData[primaryBreakdownValue][secondaryBreakdownValue] = datum;
        } else {
          processedData[primaryBreakdownValue] = {
            [secondaryBreakdownValue]: datum,
          };
        }
      } else {
        if (processedData[primaryBreakdownValue]) {
          processedData[primaryBreakdownValue] = datum;
        } else {
          processedData[primaryBreakdownValue] = datum;
        }
      }

      return processedData;
    },
    {},
  );
}

function flattenFinalOrderedMap(finalMap: OrderedFinalBreakdownMap) {
  return flatten(Array.from(finalMap.values()));
}

function addDatumFromLookupMapOrZeroSpendDatumToFinalMap(
  lookupMap: ProcessedRecommendedData,
  finalMap: OrderedFinalBreakdownMap,
  trackingSet: BreakdownDatumTrackingSet,
  breakdowns: BudgetMixBreakdownSpendDatum["breakdowns"],
  dataColumnName: string,
) {
  const breakdownValues = breakdowns.map(({ value }) => value);
  const lookupValue = get(lookupMap, breakdownValues.join("."));

  if (lookupValue) {
    const recommendedDatumToAdd = lookupValue as BudgetMixBreakdownSpendDatum;
    addDatumToPrimaryBreakdownMap(
      finalMap,
      recommendedDatumToAdd as BudgetMixBreakdownSpendDatum,
      breakdowns[0].value,
    );
    trackingSet.delete(JSON.stringify(recommendedDatumToAdd));
  } else {
    const zeroSpendDatum = createDatumWithZeroSpend(breakdowns, dataColumnName);
    addDatumToPrimaryBreakdownMap(
      finalMap,
      zeroSpendDatum,
      breakdowns[0].value,
    );
  }
}

function createDatumWithZeroSpend(
  breakdowns: BudgetMixBreakdownSpendDatum["breakdowns"],
  dataColumnName: string,
): BudgetMixBreakdownSpendDatum {
  return {
    breakdowns,
    spend: 0,
    data_column_name: dataColumnName,
  };
}

function addDatumToPrimaryBreakdownMap(
  map: Map<string, BudgetMixBreakdownSpendDatum[]>,
  datum: BudgetMixBreakdownSpendDatum,
  primaryBreakdownValue: string,
) {
  const datasetForBreakdownValue = map.get(primaryBreakdownValue);
  if (datasetForBreakdownValue) {
    datasetForBreakdownValue.push(datum);
  } else {
    map.set(primaryBreakdownValue, [datum]);
  }
}
